import { Track } from "../audio/Track.js";
import { Timeline } from "../timeline/Timeline.js";
import { ViewportRenderer } from "../rendering/ViewportRenderer.js";
import { WaveformGenerator } from "../waveform/WaveformGenerator.js";
import { extractOriginalSampleRate } from "../audio/AudioFileUtils.js";

export class AudioDAW {
  constructor(canvasElement, options = {}) {
    this.canvas = canvasElement;
    this.minimapCanvas = options.minimapCanvas || null;

    // Audio engine
    this.audioContext = null;
    this.audioSources = new Map();
    this.isPlaying = false;
    this.currentTime = 0;
    this.currentTimeOffset = 0;
    this.animationFrameId = null;
    this.followPlayhead =
      options.followPlayhead !== undefined ? options.followPlayhead : true;

    // Logging configuration
    this.enableLogging =
      options.enableLogging !== undefined ? options.enableLogging : false;
    this.logFunction =
      options.logFunction ||
      ((message) => console.log(`[AudioDAW] ${message}`));

    // Timeline and renderer
    this.timeline = new Timeline();
    this.timeline.pixelsPerSecond = options.pixelsPerSecond || 50;
    this.renderer = new ViewportRenderer(
      this.canvas,
      this.timeline,
      this,
      options.theme
    );

    if (this.minimapCanvas) {
      this.renderer.setMinimapCanvas(this.minimapCanvas);
    }

    // Event callbacks
    this.onLoadingStateChange = options.onLoadingStateChange || null;
    this.onPlayStateChange = options.onPlayStateChange || null;
    this.onTimeUpdate = options.onTimeUpdate || null;
    this.onSelectionChange = options.onSelectionChange || null;
    this.onRegionsChange = options.onRegionsChange || null;
    this.onTrackAdded = options.onTrackAdded || null;
    this.onTrackRemoved = options.onTrackRemoved || null;
    this.onTrackOffsetChange = options.onTrackOffsetChange || null;
    this.onTrackVolumeChange = options.onTrackVolumeChange || null;
    this.onTrackPanChange = options.onTrackPanChange || null;
    this.onTrackGainChange = options.onTrackGainChange || null;
    this.onTrackCompressorChange = options.onTrackCompressorChange || null;
    this.onTrackColorChange = options.onTrackColorChange || null;
    this.onZoomChange = options.onZoomChange || null;
    this.onDurationChange = options.onDurationChange || null;
    this.onScrollChange = options.onScrollChange || null;
    this.onActiveRegionGroupChange = options.onActiveRegionGroupChange || null;
    this.onRegionGroupsChange = options.onRegionGroupsChange || null;
  }

  // ============ LOGGING ============

  /**
   * Internal logging function
   * @private
   */
  log(message) {
    if (this.enableLogging && this.logFunction) {
      this.logFunction(message);
    }
  }

  /**
   * Set logging enabled state
   * @param {boolean} enabled - Enable or disable logging
   */
  setLoggingEnabled(enabled) {
    this.enableLogging = enabled;
  }

  /**
   * Get logging enabled state
   * @returns {boolean} Whether logging is enabled
   */
  isLoggingEnabled() {
    return this.enableLogging;
  }

  /**
   * Set custom log function
   * @param {Function} logFunction - Custom logging function that receives a message string
   */
  setLogFunction(logFunction) {
    this.logFunction = logFunction;
  }

  // ============ PUBLIC API METHODS ============

  /**
   * Get all tracks with their properties
   * @returns {Array} Array of track objects with id, fileName, duration, timeOffset, volume, isLoading, loadingPhase
   */
  getTracks() {
    return this.timeline.tracks.map((track) => ({
      id: track.id,
      fileName: track.fileName,
      duration: track.audioBuffer ? track.audioBuffer.duration : 0,
      timeOffset: track.timeOffset,
      sampleOffset: track.sampleOffset,
      sampleRate: track.audioBuffer ? track.audioBuffer.sampleRate : 0,
      originalSampleRate: track.originalSampleRate,
      volume: track.volume,
      pan: track.pan,
      isLoading: track.isLoading,
      loadingPhase: track.loadingPhase,
      color: track.color,
    }));
  }

  /**
   * Add a new track with audio data
   * @param {File|ArrayBuffer} audioData - Audio file or buffer
   * @param {Object} options - Track options (fileName, timeOffset, volume, color)
   * @returns {Promise<string>} Track ID
   */
  async addTrack(audioData, options = {}) {
    await this.initAudioContext();

    const trackId = options.id || `track-${Date.now()}`;

    // Create track with loading state
    const newTrack = new Track(trackId);
    newTrack.fileName = options.fileName || "Untitled";
    newTrack.timeOffset = options.timeOffset || 0;
    newTrack.volume = options.volume !== undefined ? options.volume : 1.0;
    newTrack.pan = options.pan !== undefined ? options.pan : 0;
    newTrack.color = options.color || "#2196F3";
    newTrack.isLoading = true;
    newTrack.loadingPhase = "audio";

    // Add track to timeline immediately to show loading state
    this.timeline.tracks.push(newTrack);
    this.renderer.scheduleRender();

    if (this.onLoadingStateChange) {
      this.onLoadingStateChange(trackId, true, "audio");
    }

    try {
      // Load audio data
      let arrayBuffer;
      if (audioData instanceof File) {
        arrayBuffer = await audioData.arrayBuffer();
      } else {
        arrayBuffer = audioData;
      }

      // Extract original sample rate before browser resampling
      const originalSampleRate = extractOriginalSampleRate(arrayBuffer);

      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      // Store original sample rate in track
      if (originalSampleRate) {
        newTrack.originalSampleRate = originalSampleRate;
        this.log(
          `Track "${newTrack.fileName}" original sample rate: ${originalSampleRate} Hz (browser resampled to: ${audioBuffer.sampleRate} Hz)`
        );
      } else {
        // Fall back to audioBuffer sample rate if we couldn't extract original
        newTrack.originalSampleRate = audioBuffer.sampleRate;
        this.log(
          `Track "${newTrack.fileName}" sample rate: ${audioBuffer.sampleRate} Hz (original rate could not be determined)`
        );
      }

      // Set project sample rate from first track
      this.timeline.setProjectSampleRate(audioBuffer.sampleRate);

      // Create audio chain: source -> preGain -> compressor (optional) -> gain (volume) -> pan -> destination

      // Pre-gain node (dB boost/cut for quiet speech)
      const preGainNode = this.audioContext.createGain();
      const gainLinear = Math.pow(10, newTrack.gain / 20); // Convert dB to linear
      preGainNode.gain.value = gainLinear;

      // Compressor node (optional)
      let compressorNode = null;
      if (newTrack.useCompressor) {
        compressorNode = this.audioContext.createDynamicsCompressor();
        // Settings optimized for speech
        compressorNode.threshold.value = -24; // dB
        compressorNode.knee.value = 30; // dB
        compressorNode.ratio.value = 12; // Compression ratio
        compressorNode.attack.value = 0.003; // seconds
        compressorNode.release.value = 0.25; // seconds
      }

      // Volume gain node (linear 0-1)
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = newTrack.volume;

      // Pan node
      const panNode = this.audioContext.createStereoPanner();
      panNode.pan.value = newTrack.pan;

      // Connect the chain
      let currentNode = preGainNode;
      if (compressorNode) {
        preGainNode.connect(compressorNode);
        currentNode = compressorNode;
      }
      currentNode.connect(gainNode);
      gainNode.connect(panNode);
      panNode.connect(this.audioContext.destination);

      // Switch to waveform generation phase
      newTrack.loadingPhase = "waveform";
      this.renderer.scheduleRender();

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, true, "waveform");
      }

      // Generate waveform data (with new mipmap levels)
      const waveformData = WaveformGenerator.generateWaveformData(audioBuffer);

      // Update track
      newTrack.audioBuffer = audioBuffer;
      newTrack.waveformData = waveformData;
      newTrack.preGainNode = preGainNode;
      newTrack.compressorNode = compressorNode;
      newTrack.gainNode = gainNode;
      newTrack.panNode = panNode;
      newTrack.isLoading = false;
      newTrack.loadingPhase = null;

      // Initialize sample offset from time offset
      newTrack.setSampleOffset(
        Math.round(newTrack.timeOffset * audioBuffer.sampleRate)
      );

      // Recalculate timeline duration
      this.recalculateDuration();

      this.renderer.scheduleRender();

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, false, null);
      }

      if (this.onTrackAdded) {
        this.onTrackAdded(trackId, {
          id: newTrack.id,
          fileName: newTrack.fileName,
          duration: newTrack.audioBuffer.duration,
          timeOffset: newTrack.timeOffset,
          volume: newTrack.volume,
          color: newTrack.color,
        });
      }

      return trackId;
    } catch (error) {
      // Remove track on error
      const index = this.timeline.tracks.findIndex((t) => t.id === trackId);
      if (index !== -1) {
        this.timeline.tracks.splice(index, 1);
      }

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, false, null);
      }

      this.renderer.scheduleRender();
      throw error;
    }
  }

  /**
   * Replace audio data on an existing track (keeps offset and settings)
   * @param {string} trackId - Track ID
   * @param {File|ArrayBuffer} audioData - New audio data
   * @returns {Promise<void>}
   */
  async setTrackAudio(trackId, audioData) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    track.isLoading = true;
    track.loadingPhase = "audio";
    this.renderer.scheduleRender();

    if (this.onLoadingStateChange) {
      this.onLoadingStateChange(trackId, true, "audio");
    }

    try {
      // Load audio data
      let arrayBuffer;
      if (audioData instanceof File) {
        arrayBuffer = await audioData.arrayBuffer();
      } else {
        arrayBuffer = audioData;
      }

      // Extract original sample rate before browser resampling
      const originalSampleRate = extractOriginalSampleRate(arrayBuffer);

      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      // Store original sample rate in track
      if (originalSampleRate) {
        track.originalSampleRate = originalSampleRate;
        this.log(
          `Track "${track.fileName}" original sample rate: ${originalSampleRate} Hz (browser resampled to: ${audioBuffer.sampleRate} Hz)`
        );
      } else {
        // Fall back to audioBuffer sample rate if we couldn't extract original
        track.originalSampleRate = audioBuffer.sampleRate;
        this.log(
          `Track "${track.fileName}" sample rate: ${audioBuffer.sampleRate} Hz (original rate could not be determined)`
        );
      }

      // Switch to waveform generation phase
      track.loadingPhase = "waveform";
      this.renderer.scheduleRender();

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, true, "waveform");
      }

      // Generate new waveform data
      const waveformData = WaveformGenerator.generateWaveformData(audioBuffer);

      // Stop old audio source if playing
      if (this.isPlaying) {
        const oldSource = this.audioSources.get(trackId);
        if (oldSource) {
          try {
            oldSource.stop();
          } catch (e) {
            // Ignore
          }
          this.audioSources.delete(trackId);
        }
      }

      // Update track (keeps timeOffset, volume, color, gainNode)
      track.audioBuffer = audioBuffer;
      track.waveformData = waveformData;
      track.isLoading = false;
      track.loadingPhase = null;

      // Recalculate timeline duration (audio length may have changed)
      this.recalculateDuration();

      // Restart playback for this track if playing
      if (this.isPlaying) {
        this.startTrackPlayback(track);
      }

      this.renderer.scheduleRender();

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, false, null);
      }
    } catch (error) {
      track.isLoading = false;
      track.loadingPhase = null;

      if (this.onLoadingStateChange) {
        this.onLoadingStateChange(trackId, false, null);
      }

      this.renderer.scheduleRender();
      throw error;
    }
  }

  /**
   * Set volume for a specific track
   * @param {string} trackId - Track ID
   * @param {number} volume - Volume level (0.0 to 1.0)
   */
  setTrackVolume(trackId, volume) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    volume = Math.max(0, Math.min(1, volume));
    track.volume = volume;

    if (track.gainNode) {
      track.gainNode.gain.setTargetAtTime(
        volume,
        this.audioContext.currentTime,
        0.01
      );
    }

    if (this.onTrackVolumeChange) {
      this.onTrackVolumeChange(trackId, volume);
    }
  }

  /**
   * Get volume for a specific track
   * @param {string} trackId - Track ID
   * @returns {number} Volume level (0.0 to 1.0)
   */
  getTrackVolume(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.volume;
  }

  /**
   * Set pan for a specific track
   * @param {string} trackId - Track ID
   * @param {number} pan - Pan value (-1.0 = left, 0.0 = center, 1.0 = right)
   */
  setTrackPan(trackId, pan) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    pan = Math.max(-1, Math.min(1, pan));
    track.pan = pan;

    if (track.panNode) {
      track.panNode.pan.setTargetAtTime(
        pan,
        this.audioContext.currentTime,
        0.01
      );
    }

    if (this.onTrackPanChange) {
      this.onTrackPanChange(trackId, pan);
    }
  }

  /**
   * Get pan for a specific track
   * @param {string} trackId - Track ID
   * @returns {number} Pan value (-1.0 to 1.0)
   */
  getTrackPan(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.pan;
  }

  /**
   * Set gain for a specific track (in dB)
   * @param {string} trackId - Track ID
   * @param {number} gainDb - Gain in decibels (-40 to +40, 0 = unity gain)
   */
  setTrackGain(trackId, gainDb) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    gainDb = Math.max(-40, Math.min(40, gainDb));
    track.gain = gainDb;

    if (track.preGainNode) {
      const gainLinear = Math.pow(10, gainDb / 20); // Convert dB to linear
      track.preGainNode.gain.setTargetAtTime(
        gainLinear,
        this.audioContext.currentTime,
        0.01
      );
    }

    // Schedule a render since gain affects waveform display when waveformGainScaling is enabled
    this.renderer.scheduleRender();

    if (this.onTrackGainChange) {
      this.onTrackGainChange(trackId, gainDb);
    }
  }

  /**
   * Get gain for a specific track (in dB)
   * @param {string} trackId - Track ID
   * @returns {number} Gain in decibels
   */
  getTrackGain(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.gain;
  }

  /**
   * Enable or disable compressor for a specific track
   * @param {string} trackId - Track ID
   * @param {boolean} enabled - Enable compressor
   * @param {Object} settings - Optional compressor settings (threshold, knee, ratio, attack, release)
   */
  setTrackCompressor(trackId, enabled, settings = {}) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    // If switching compressor state, need to rebuild audio chain
    if (track.useCompressor !== enabled) {
      track.useCompressor = enabled;

      // Disconnect existing chain
      if (track.panNode) track.panNode.disconnect();
      if (track.gainNode) track.gainNode.disconnect();
      if (track.compressorNode) track.compressorNode.disconnect();
      if (track.preGainNode) track.preGainNode.disconnect();

      // Recreate compressor if enabling
      if (enabled) {
        track.compressorNode = this.audioContext.createDynamicsCompressor();
        track.compressorNode.threshold.value = settings.threshold || -24;
        track.compressorNode.knee.value = settings.knee || 30;
        track.compressorNode.ratio.value = settings.ratio || 12;
        track.compressorNode.attack.value = settings.attack || 0.003;
        track.compressorNode.release.value = settings.release || 0.25;
      } else {
        track.compressorNode = null;
      }

      // Reconnect chain
      let currentNode = track.preGainNode;
      if (track.compressorNode) {
        track.preGainNode.connect(track.compressorNode);
        currentNode = track.compressorNode;
      }
      currentNode.connect(track.gainNode);
      track.gainNode.connect(track.panNode);
      track.panNode.connect(this.audioContext.destination);

      // Restart playback if playing
      if (this.isPlaying) {
        const wasPlaying = true;
        this.stop();
        if (wasPlaying) {
          this.play();
        }
      }
    } else if (enabled && track.compressorNode && settings) {
      // Just update settings
      if (settings.threshold !== undefined)
        track.compressorNode.threshold.value = settings.threshold;
      if (settings.knee !== undefined)
        track.compressorNode.knee.value = settings.knee;
      if (settings.ratio !== undefined)
        track.compressorNode.ratio.value = settings.ratio;
      if (settings.attack !== undefined)
        track.compressorNode.attack.value = settings.attack;
      if (settings.release !== undefined)
        track.compressorNode.release.value = settings.release;
    }

    if (this.onTrackCompressorChange) {
      this.onTrackCompressorChange(trackId, enabled, settings);
    }
  }

  /**
   * Check if compressor is enabled for a track
   * @param {string} trackId - Track ID
   * @returns {boolean} Whether compressor is enabled
   */
  getTrackCompressor(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.useCompressor;
  }

  /**
   * Set color for a specific track
   * @param {string} trackId - Track ID
   * @param {string} color - CSS color string
   */
  setTrackColor(trackId, color) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    track.color = color;
    this.renderer.scheduleRender();

    if (this.onTrackColorChange) {
      this.onTrackColorChange(trackId, color);
    }
  }

  /**
   * Get color for a specific track
   * @param {string} trackId - Track ID
   * @returns {string} CSS color string
   */
  getTrackColor(trackId) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }
    return track.color;
  }

  /**
   * Remove a track
   * @param {string} trackId - Track ID
   */
  removeTrack(trackId) {
    const index = this.timeline.tracks.findIndex((t) => t.id === trackId);
    if (index === -1) {
      throw new Error(`Track ${trackId} not found`);
    }

    const track = this.timeline.tracks[index];

    // Stop audio source if playing
    if (this.isPlaying) {
      const source = this.audioSources.get(trackId);
      if (source) {
        try {
          source.stop();
        } catch (e) {
          // Ignore
        }
        this.audioSources.delete(trackId);
      }
    }

    // Disconnect audio nodes
    if (track.panNode) track.panNode.disconnect();
    if (track.gainNode) track.gainNode.disconnect();
    if (track.compressorNode) track.compressorNode.disconnect();
    if (track.preGainNode) track.preGainNode.disconnect();

    // Remove from timeline
    this.timeline.tracks.splice(index, 1);

    // Recalculate duration
    this.recalculateDuration();

    this.renderer.scheduleRender();

    if (this.onTrackRemoved) {
      this.onTrackRemoved(trackId);
    }
  }

  /**
   * Set time offset for a track
   * @param {string} trackId - Track ID
   * @param {number} timeOffset - Time offset in seconds
   */
  setTrackOffset(trackId, timeOffset) {
    const track = this.timeline.tracks.find((t) => t.id === trackId);
    if (!track) {
      throw new Error(`Track ${trackId} not found`);
    }

    track.timeOffset = Math.max(0, timeOffset);

    // Recalculate duration
    this.recalculateDuration();

    // Restart playback if playing
    if (this.isPlaying) {
      this.stop();
      this.play();
    }

    this.renderer.scheduleRender();

    if (this.onTrackOffsetChange) {
      this.onTrackOffsetChange(trackId, track.timeOffset);
    }
  }

  /**
   * Start playback
   */
  async play() {
    if (this.isPlaying) return;

    await this.initAudioContext();

    // Start all tracks
    for (const track of this.timeline.tracks) {
      if (!track.audioBuffer || track.isLoading) continue;
      this.startTrackPlayback(track);
    }

    this.currentTimeOffset = this.audioContext.currentTime - this.currentTime;
    this.isPlaying = true;

    if (this.onPlayStateChange) {
      this.onPlayStateChange(true);
    }

    this.updatePlayhead();
  }

  /**
   * Stop playback
   */
  stop() {
    if (!this.isPlaying) return;

    // Stop all audio sources
    for (const source of this.audioSources.values()) {
      try {
        source.stop();
      } catch (e) {
        // Ignore
      }
    }
    this.audioSources.clear();

    this.isPlaying = false;

    if (this.onPlayStateChange) {
      this.onPlayStateChange(false);
    }

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  /**
   * Reset playhead to start
   */
  reset() {
    this.stop();
    this.currentTime = 0;
    this.renderer.scheduleRender();

    if (this.onTimeUpdate) {
      this.onTimeUpdate(0);
    }
  }

  /**
   * Seek to specific time
   * @param {number} time - Time in seconds
   */
  seek(time) {
    const wasPlaying = this.isPlaying;

    if (wasPlaying) {
      this.stop();
    }

    this.currentTime = Math.max(0, Math.min(this.timeline.duration, time));
    this.currentTimeOffset = this.audioContext
      ? this.audioContext.currentTime - this.currentTime
      : 0;

    if (wasPlaying) {
      this.play();
    }

    this.renderer.scheduleRender();

    if (this.onTimeUpdate) {
      this.onTimeUpdate(this.currentTime);
    }
  }

  /**
   * Set zoom level (pixels per second)
   * @param {number} pixelsPerSecond - Zoom level
   */
  setZoom(pixelsPerSecond) {
    this.timeline.pixelsPerSecond = Math.min(
      500,
      Math.max(0.1, pixelsPerSecond)
    );
    this.timeline.updateViewport(this.renderer.canvas.offsetWidth);

    const visibleDuration = this.timeline.getVisibleDuration();
    const effectiveDuration = this.timeline.getEffectiveDuration(
      this.renderer.canvas.offsetWidth
    );
    const maxScroll = Math.max(0, effectiveDuration - visibleDuration);

    this.timeline.viewportScrollTime = Math.max(
      0,
      Math.min(maxScroll, this.timeline.viewportScrollTime)
    );
    this.timeline.updateViewport(this.renderer.canvas.offsetWidth);

    this.renderer.scheduleRender();

    if (this.onZoomChange) {
      this.onZoomChange(this.timeline.pixelsPerSecond);
    }
  }

  /**
   * Get current zoom level
   * @returns {number} Pixels per second
   */
  getZoom() {
    return this.timeline.pixelsPerSecond;
  }

  /**
   * Zoom to fit all content
   */
  zoomToFit() {
    if (this.timeline.duration === 0) return;

    const containerWidth = this.canvas.offsetWidth;
    const targetZoom = containerWidth / this.timeline.duration;
    this.setZoom(targetZoom);

    this.timeline.viewportScrollTime = 0;
    this.timeline.updateViewport(this.renderer.canvas.offsetWidth);
    this.renderer.scheduleRender();
  }

  /**
   * Get current playback time
   * @returns {number} Current time in seconds
   */
  getCurrentTime() {
    return this.currentTime;
  }

  /**
   * Get total duration
   * @returns {number} Duration in seconds
   */
  getDuration() {
    return this.timeline.duration;
  }

  /**
   * Update the visual theme
   * @param {Object} theme - Theme configuration object (partial updates supported)
   */
  setTheme(theme) {
    this.renderer.setTheme(theme);
  }

  /**
   * Get the current theme
   * @returns {Object} Current theme configuration
   */
  getTheme() {
    return { ...this.renderer.theme };
  }

  /**
   * Enable or disable waveform gain scaling
   * When enabled, waveforms visually scale with gain adjustments using soft clipping
   * @param {boolean} enabled - Whether to enable waveform gain scaling
   */
  setWaveformGainScaling(enabled) {
    this.renderer.theme.waveformGainScaling = enabled;
    this.renderer.scheduleRender();
  }

  /**
   * Get whether waveform gain scaling is enabled
   * @returns {boolean} Whether waveform gain scaling is enabled
   */
  getWaveformGainScaling() {
    return this.renderer.theme.waveformGainScaling;
  }

  /**
   * Set selection range
   * @param {number} start - Start time in seconds
   * @param {number} end - End time in seconds
   */
  setSelection(start, end) {
    if (start < 0 || end < 0 || start > end) {
      throw new Error("Invalid selection range");
    }

    this.timeline.selection = {
      start,
      end,
      startSample: this.timeline.timeToSamples(start),
      endSample: this.timeline.timeToSamples(end),
    };
    this.renderer.scheduleRender();

    if (this.onSelectionChange) {
      this.onSelectionChange(this.timeline.selection);
    }
  }

  /**
   * Get current selection
   * @returns {Object|null} Selection object with start and end, or null if no selection
   */
  getSelection() {
    return this.timeline.selection ? { ...this.timeline.selection } : null;
  }

  /**
   * Clear current selection
   */
  clearSelection() {
    this.timeline.selection = null;
    this.renderer.scheduleRender();

    if (this.onSelectionChange) {
      this.onSelectionChange(null);
    }
  }

  // ============ REGION GROUP MANAGEMENT ============

  /**
   * Create a new region group
   * @param {string} name - Optional name for the region group
   * @param {Object} options - Optional configuration
   * @param {number|null} options.maxRegions - Maximum number of regions (null = unlimited)
   * @param {boolean} options.readOnly - Whether the group is read-only
   * @returns {string} Group ID
   */
  createRegionGroup(name = "", options = {}) {
    const groupId = this.timeline.createRegionGroup(name, options);

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }

    return groupId;
  }

  /**
   * Set the active region group
   * @param {string} groupId - Group ID to set as active
   */
  setActiveRegionGroup(groupId) {
    if (!this.timeline.regionGroups.has(groupId)) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    this.timeline.activeRegionGroupId = groupId;
    this.renderer.scheduleRender();

    if (this.onActiveRegionGroupChange) {
      this.onActiveRegionGroupChange(groupId);
    }
  }

  /**
   * Get the active region group ID
   * @returns {string|null} Active group ID or null if none
   */
  getActiveRegionGroupId() {
    return this.timeline.activeRegionGroupId;
  }

  /**
   * Get all region groups
   * @returns {Array} Array of {id, name, regions, maxRegions, readOnly} objects
   */
  getRegionGroups() {
    const groups = [];
    for (const [id, group] of this.timeline.regionGroups.entries()) {
      groups.push({
        id,
        name: group.name,
        regions: group.regions.map((r) => ({ ...r })),
        maxRegions: group.maxRegions,
        readOnly: group.readOnly,
      });
    }
    return groups;
  }

  /**
   * Get regions from a specific group
   * @param {string} groupId - Group ID
   * @returns {Array} Array of {start, end} objects
   */
  getRegions(groupId) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    return group.regions.map((r) => ({ ...r }));
  }

  /**
   * Get regions from the active group
   * @returns {Array} Array of {start, end, startSample, endSample} objects
   */
  getActiveRegions() {
    return this.timeline.getActiveRegions().map((r) => ({ ...r }));
  }

  /**
   * Get the project sample rate (set from first track added)
   * @returns {number|null} Sample rate in Hz, or null if no tracks loaded
   */
  getProjectSampleRate() {
    return this.timeline.projectSampleRate;
  }

  /**
   * Set name for a region group
   * @param {string} groupId - Group ID
   * @param {string} name - New name
   */
  setRegionGroupName(groupId, name) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    group.name = name;
  }

  /**
   * Remove a region group
   * @param {string} groupId - Group ID to remove
   */
  removeRegionGroup(groupId) {
    if (!this.timeline.regionGroups.has(groupId)) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }

    this.timeline.regionGroups.delete(groupId);

    // If we deleted the active group, clear the active group
    if (this.timeline.activeRegionGroupId === groupId) {
      this.timeline.activeRegionGroupId = null;

      if (this.onActiveRegionGroupChange) {
        this.onActiveRegionGroupChange(null);
      }
    }

    if (this.onRegionGroupsChange) {
      this.onRegionGroupsChange(this.getRegionGroups());
    }

    this.renderer.scheduleRender();
  }

  /**
   * Clear all regions from the active group
   */
  clearActiveRegions() {
    if (!this.timeline.activeRegionGroupId) return;

    const group = this.timeline.regionGroups.get(
      this.timeline.activeRegionGroupId
    );
    if (group) {
      group.regions = [];
      this.renderer.scheduleRender();

      if (this.onRegionsChange) {
        this.onRegionsChange(this.timeline.activeRegionGroupId);
      }
    }
  }

  /**
   * Add a region to the active group
   * @param {number} start - Start time in seconds
   * @param {number} end - End time in seconds
   */
  addRegion(start, end) {
    if (start < 0 || end < 0 || start > end) {
      throw new Error("Invalid region range");
    }

    this.timeline.addRegionToActiveGroup(start, end);
    this.renderer.scheduleRender();

    if (this.onRegionsChange) {
      this.onRegionsChange(this.timeline.activeRegionGroupId);
    }
  }

  /**
   * Set read-only mode for a region group
   * @param {string} groupId - Group ID
   * @param {boolean} readOnly - Whether the group should be read-only
   */
  setRegionGroupReadOnly(groupId, readOnly) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    group.readOnly = readOnly;
    this.renderer.scheduleRender();
  }

  /**
   * Set maximum regions for a region group
   * @param {string} groupId - Group ID
   * @param {number|null} maxRegions - Maximum number of regions (null = unlimited)
   */
  setRegionGroupMaxRegions(groupId, maxRegions) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    if (
      maxRegions !== null &&
      (typeof maxRegions !== "number" || maxRegions < 0)
    ) {
      throw new Error("maxRegions must be a positive number or null");
    }
    group.maxRegions = maxRegions;
  }

  /**
   * Get configuration options for a region group
   * @param {string} groupId - Group ID
   * @returns {Object} Object with {maxRegions, readOnly} properties
   */
  getRegionGroupOptions(groupId) {
    const group = this.timeline.regionGroups.get(groupId);
    if (!group) {
      throw new Error(`Region group '${groupId}' does not exist`);
    }
    return {
      maxRegions: group.maxRegions,
      readOnly: group.readOnly,
    };
  }

  // ============ INTERNAL METHODS ============

  async initAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
    }
  }

  startTrackPlayback(track) {
    const source = this.audioContext.createBufferSource();
    source.buffer = track.audioBuffer;
    // Connect to the first node in the chain (preGainNode)
    source.connect(track.preGainNode);

    const timeOffset = track.timeOffset;
    const timePlayOffset = timeOffset - this.currentTime;
    const when = timePlayOffset > 0 ? timePlayOffset : 0;
    const offset = timePlayOffset > 0 ? 0 : -timePlayOffset;

    if (offset < track.audioBuffer.duration) {
      source.start(when + this.audioContext.currentTime, offset);
      this.audioSources.set(track.id, source);
    }
  }

  updatePlayhead() {
    if (!this.isPlaying) return;

    const currentTime = this.audioContext.currentTime - this.currentTimeOffset;
    this.currentTime = currentTime;

    if (this.onTimeUpdate) {
      this.onTimeUpdate(currentTime);
    }

    // Auto-scroll to follow playhead if enabled
    if (this.followPlayhead && this.renderer && this.timeline) {
      const playheadX = this.timeline.timeToCanvasX(this.currentTime);
      const canvasWidth = this.renderer.canvas.offsetWidth;
      const centerX = canvasWidth / 2;

      if (playheadX >= centerX) {
        const visibleDuration = this.timeline.getVisibleDuration();
        const newScrollTime = this.currentTime - visibleDuration / 2;
        this.renderer.scrollToTime(newScrollTime);
      }
    }

    this.renderer.scheduleRender();

    if (this.currentTime >= this.timeline.duration) {
      this.stop();
    } else {
      this.animationFrameId = requestAnimationFrame(() =>
        this.updatePlayhead()
      );
    }
  }

  recalculateDuration() {
    let maxDuration = 0;
    for (const track of this.timeline.tracks) {
      if (track.audioBuffer) {
        const trackEnd = track.timeOffset + track.audioBuffer.duration;
        if (trackEnd > maxDuration) {
          maxDuration = trackEnd;
        }
      }
    }
    const oldDuration = this.timeline.duration;
    this.timeline.duration = maxDuration;

    if (oldDuration !== maxDuration && this.onDurationChange) {
      this.onDurationChange(maxDuration);
    }
  }

  // ============ STATE MANAGEMENT ============

  /**
   * Get the current state of the DAW for serialization
   * Note: Audio buffers are not serialized - only metadata and settings
   * @returns {Object} Serializable state object
   */
  getState() {
    return {
      version: "1.0.0",
      timeline: {
        pixelsPerSecond: this.timeline.pixelsPerSecond,
        duration: this.timeline.duration,
        projectSampleRate: this.timeline.projectSampleRate,
        tracks: this.timeline.tracks.map((track) => ({
          id: track.id,
          fileName: track.fileName,
          timeOffset: track.timeOffset,
          sampleOffset: track.sampleOffset,
          originalSampleRate: track.originalSampleRate,
          volume: track.volume,
          gain: track.gain,
          pan: track.pan,
          useCompressor: track.useCompressor,
          color: track.color,
          // Note: audioBuffer is not serialized - must be reloaded
        })),
      },
      regionGroups: Array.from(this.timeline.regionGroups.entries()).map(
        ([id, group]) => ({
          id,
          name: group.name,
          regions: group.regions.map((r) => ({
            start: r.start,
            end: r.end,
            startSample: r.startSample,
            endSample: r.endSample,
          })),
          maxRegions: group.maxRegions,
          readOnly: group.readOnly,
        })
      ),
      activeRegionGroupId: this.timeline.activeRegionGroupId,
      playback: {
        currentTime: this.currentTime,
        isPlaying: this.isPlaying,
        followPlayhead: this.followPlayhead,
      },
      view: {
        scrollTime: this.renderer ? this.renderer.timeline.scrollTime : 0,
        verticalScrollOffset: this.renderer
          ? this.renderer.verticalScrollOffset
          : 0,
      },
      theme: this.renderer ? this.renderer.theme : null,
      settings: {
        enableLogging: this.enableLogging,
      },
    };
  }

  /**
   * Restore DAW state from a serialized state object
   * Note: Audio files must be provided separately and matched by track ID
   * @param {Object} state - State object from getState()
   * @param {Object} audioFiles - Map of track IDs to audio File/ArrayBuffer objects
   * @returns {Promise<void>}
   */
  async setState(state, audioFiles = {}) {
    if (!state || state.version !== "1.0.0") {
      throw new Error("Invalid or unsupported state version");
    }

    // Stop playback
    this.stop();

    // Clear existing tracks
    const existingTrackIds = [...this.timeline.tracks.map((t) => t.id)];
    for (const trackId of existingTrackIds) {
      this.removeTrack(trackId);
    }

    // Restore timeline settings
    this.timeline.pixelsPerSecond = state.timeline.pixelsPerSecond;
    if (state.timeline.projectSampleRate) {
      this.timeline.projectSampleRate = state.timeline.projectSampleRate;
    }
    if (this.renderer) {
      this.renderer.timeline.pixelsPerSecond = state.timeline.pixelsPerSecond;
    }

    // Restore tracks
    for (const trackState of state.timeline.tracks) {
      const audioData = audioFiles[trackState.id];

      if (audioData) {
        // Add track with audio data
        await this.addTrack(audioData, {
          id: trackState.id,
          fileName: trackState.fileName,
          timeOffset: trackState.timeOffset,
          volume: trackState.volume,
          color: trackState.color,
        });

        // Apply additional settings
        this.setTrackGain(trackState.id, trackState.gain);
        this.setTrackPan(trackState.id, trackState.pan);
        this.setTrackCompressor(trackState.id, trackState.useCompressor);

        // Restore sample offset and original sample rate if available
        const track = this.timeline.tracks.find((t) => t.id === trackState.id);
        if (track) {
          if (trackState.originalSampleRate !== undefined) {
            track.originalSampleRate = trackState.originalSampleRate;
          }
          if (trackState.sampleOffset !== undefined) {
            track.setSampleOffset(trackState.sampleOffset);
          }
        }
      } else {
        // Create placeholder track without audio (will show loading state)
        const track = new Track(trackState.id);
        track.fileName = trackState.fileName;
        track.timeOffset = trackState.timeOffset;
        track.sampleOffset = trackState.sampleOffset || 0;
        track.originalSampleRate = trackState.originalSampleRate || null;
        track.volume = trackState.volume;
        track.gain = trackState.gain;
        track.pan = trackState.pan;
        track.useCompressor = trackState.useCompressor;
        track.color = trackState.color;
        track.isLoading = true;
        track.loadingPhase = "audio";

        this.timeline.tracks.push(track);
        this.log(
          `Track created as placeholder: ${trackState.id} (${trackState.fileName}) - waiting for audio data`
        );
      }
    }

    // Clear existing region groups
    const existingGroupIds = [...this.timeline.regionGroups.map((g) => g.id)];
    for (const groupId of existingGroupIds) {
      this.removeRegionGroup(groupId);
    }

    // Restore region groups
    for (const groupState of state.regionGroups) {
      const group = this.createRegionGroup(groupState.id, groupState.name);
      group.regions = groupState.regions.map((r) => ({
        start: r.start,
        end: r.end,
      }));
      group.maxRegions = groupState.maxRegions;
      group.readOnly = groupState.readOnly;
      this.timeline.regionGroups.push(group);
    }

    // Restore active region group
    if (state.activeRegionGroupId) {
      this.setActiveRegionGroup(state.activeRegionGroupId);
    }

    // Restore playback state
    this.followPlayhead = state.playback.followPlayhead;
    if (state.playback.currentTime > 0) {
      this.seek(state.playback.currentTime);
    }

    // Restore view state
    if (this.renderer && state.view) {
      if (state.view.scrollTime !== undefined) {
        this.renderer.scrollToTime(state.view.scrollTime);
      }
      if (state.view.verticalScrollOffset !== undefined) {
        this.renderer.verticalScrollOffset = state.view.verticalScrollOffset;
      }
    }

    // Restore theme
    if (state.theme) {
      this.setTheme(state.theme);
    }

    // Restore settings
    if (state.settings) {
      this.setLoggingEnabled(state.settings.enableLogging);
    }

    // Recalculate duration
    this.recalculateDuration();

    // Render
    if (this.renderer) {
      this.renderer.scheduleRender();
    }

    this.log("State restored successfully");
  }

  // ============ LIFECYCLE ============

  /**
   * Clean up all resources and event listeners
   * Call this when unmounting/removing the DAW instance
   */
  destroy() {
    this.log("Destroying AudioDAW instance");

    // Stop playback and cancel animation frames
    this.stop();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Clear audio sources
    for (const source of this.audioSources.values()) {
      try {
        source.stop();
        source.disconnect();
      } catch (e) {
        // Ignore if already stopped
      }
    }
    this.audioSources.clear();

    // Disconnect all audio nodes
    for (const track of this.timeline.tracks) {
      if (track.gainNode) {
        track.gainNode.disconnect();
        track.gainNode = null;
      }
      if (track.preGainNode) {
        track.preGainNode.disconnect();
        track.preGainNode = null;
      }
      if (track.panNode) {
        track.panNode.disconnect();
        track.panNode = null;
      }
      if (track.compressorNode) {
        track.compressorNode.disconnect();
        track.compressorNode = null;
      }
    }

    // Close audio context
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Clean up renderer if it has a destroy method
    if (this.renderer && typeof this.renderer.destroy === "function") {
      this.renderer.destroy();
    }

    // Clean up context menu
    if (this.renderer && this.renderer.hideContextMenu) {
      this.renderer.hideContextMenu();
    }

    // Clear references
    this.timeline = null;
    this.renderer = null;
    this.canvas = null;
    this.minimapCanvas = null;

    // Clear callbacks
    this.onLoadingStateChange = null;
    this.onPlayStateChange = null;
    this.onTimeUpdate = null;
    this.onSelectionChange = null;
    this.onRegionsChange = null;
    this.onTrackAdded = null;
    this.onTrackRemoved = null;
    this.onTrackOffsetChange = null;
    this.onTrackVolumeChange = null;
    this.onTrackPanChange = null;
    this.onTrackGainChange = null;
    this.onTrackCompressorChange = null;
    this.onTrackColorChange = null;
    this.onZoomChange = null;
    this.onDurationChange = null;
    this.onScrollChange = null;
    this.onActiveRegionGroupChange = null;
    this.onRegionGroupsChange = null;

    this.log("AudioDAW instance destroyed");
  }
}
